import { useState, useEffect } from "react";
import { useLang } from "@/context/LanguageContext";
import { resolveImg } from "@/lib/api";
import { scrollToId } from "@/lib/scroll";
import { motion } from "framer-motion";

const NAV = [
  { id: "menu", key: "nav_menu" },
  { id: "story", key: "nav_story" },
  { id: "events", key: "nav_events" },
  { id: "gallery", key: "nav_gallery" },
  { id: "visit", key: "nav_visit" },
];

export const Header = ({ settings, promoVisible }) => {
  const { lang, toggle, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      data-testid="site-header"
      className={`fixed left-0 right-0 z-50 transition-[background-color,border-color,padding,top] duration-500 ${
        promoVisible ? "top-10" : "top-0"
      } ${
        scrolled ? "bg-dough/85 backdrop-blur-xl border-b border-ink/10 py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 flex items-center justify-between gap-4">
        <button onClick={() => go("top")} className="flex items-center gap-3 group" data-testid="header-logo">
          <img
            src={resolveImg(settings?.logo_url)}
            alt="La Panadería Totopan"
            className="h-11 w-11 rounded-full object-cover ring-2 ring-ink/15 transition-transform duration-500 group-hover:rotate-6"
          />
          <span className="font-serif text-lg md:text-xl leading-none tracking-tight text-ink hidden sm:block">
            La Panadería <span className="italic">Totopan</span>
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-8">
          {NAV.map((n) => (
            <button
              key={n.id}
              onClick={() => go(n.id)}
              data-testid={`nav-${n.id}`}
              className="font-label text-sm font-medium tracking-wide text-ink/80 hover:text-ink link-underline"
            >
              {t(n.key)}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={toggle}
            data-testid="lang-toggle"
            className="font-label text-xs font-bold tracking-wider border border-ink/25 rounded-full px-3 py-1.5 hover:bg-ink hover:text-dough transition-colors duration-300"
          >
            {lang === "en" ? "ES" : "EN"}
          </button>
          <button
            onClick={() => go("visit")}
            data-testid="header-order-btn"
            className="hidden sm:inline-flex font-label text-sm font-bold tracking-wide bg-terracotta text-dough rounded-full px-5 py-2.5 hover:bg-ink transition-colors duration-300"
          >
            {t("order")}
          </button>
          <button
            className="md:hidden font-label text-xs font-bold border border-ink/25 rounded-full px-3 py-2"
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
          >
            {open ? "✕" : "☰"}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden bg-dough border-t border-ink/10 px-6 py-4 flex flex-col gap-3" data-testid="mobile-nav">
          {NAV.map((n) => (
            <button key={n.id} onClick={() => go(n.id)} className="text-left font-label font-medium text-ink py-1">
              {t(n.key)}
            </button>
          ))}
          <button onClick={() => go("visit")} className="mt-2 bg-terracotta text-dough font-label font-bold rounded-full px-5 py-2.5">
            {t("order")}
          </button>
        </div>
      )}
    </motion.header>
  );
};
